"use client"

import { useState, useEffect } from "react"
import { Modal, Form, InputGroup, Card, Badge, Button } from "react-bootstrap"
import { Search, User, Building } from "lucide-react"
import { useTranslation } from "../hooks/use-translation"
import { useAppSelector } from "../redux/hooks"
import { compareBuildingNames } from "../data/buildings"

interface UserSelectionModalProps {
  show: boolean
  onHide: () => void
  onSelect: (userId: string) => void
  selectedUserId?: string
}

interface Recipient {
  id: string
  name: string
  role: string
  building?: string
  roomNumber?: string
}

export default function UserSelectionModal({ show, onHide, onSelect, selectedUserId }: UserSelectionModalProps) {
  const { t } = useTranslation()
  const { recipients } = useAppSelector((state) => state.messages)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedBuilding, setSelectedBuilding] = useState("")

  const buildings = Array.from(new Set(recipients.map((r) => r.building).filter((b): b is string => Boolean(b)))).sort(
    compareBuildingNames,
  )

  const filteredRecipients = recipients
    .filter((recipient) => {
      const matchesSearch =
        recipient.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (recipient.roomNumber && recipient.roomNumber.toLowerCase().includes(searchQuery.toLowerCase()))

      const matchesBuilding = !selectedBuilding || recipient.building === selectedBuilding

      return matchesSearch && matchesBuilding
    })
    .sort((a, b) => a.name.localeCompare(b.name))

  const handleSelect = (userId: string) => {
    onSelect(userId)
    onHide()
  }

  useEffect(() => {
    if (!show) {
      setSearchQuery("")
      setSelectedBuilding("")
    }
  }, [show])

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("selectRecipient")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <InputGroup className="mb-3">
            <InputGroup.Text className="bg-white border-end-0">
              <Search className="size-4 text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder={t("searchByNameOrApartment")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-start-0"
            />
          </InputGroup>

          <Form.Select value={selectedBuilding} onChange={(e) => setSelectedBuilding(e.target.value)}>
            <option value="">{t("allBuildings")}</option>
            {buildings.map((building) => (
              <option key={building} value={building}>
                {building}
              </option>
            ))}
          </Form.Select>
        </div>

        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          {filteredRecipients.length === 0 ? (
            <div className="text-center py-4 text-muted">{t("noUsersFound")}</div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {filteredRecipients.map((recipient) => (
                <Card
                  key={recipient.id}
                  className={`card-hover cursor-pointer ${selectedUserId === recipient.id ? "border-primary" : ""}`}
                  onClick={() => handleSelect(recipient.id)}
                >
                  <Card.Body className="py-2">
                    <div className="d-flex align-items-center gap-3">
                      <div className="flex-shrink-0">
                        <div
                          className="d-flex align-items-center justify-content-center bg-light rounded-circle"
                          style={{ width: 40, height: 40 }}
                        >
                          <User size={20} className="text-muted" />
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <h6 className="mb-0">{recipient.name}</h6>
                          {recipient.role === "admin" && (
                            <Badge bg="primary" className="small">
                              {t("admin")}
                            </Badge>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-2 text-muted small">
                          {recipient.building && (
                            <div className="d-flex align-items-center gap-1">
                              <Building size={14} />
                              <span>{recipient.building}</span>
                            </div>
                          )}
                          {recipient.roomNumber && (
                            <span>
                              • {t("apartment")} {recipient.roomNumber}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button className="modal-cancel-btn" onClick={onHide}>
          {t("cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
